import { useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { importDripLeads, importDripLeadsXlsx, importDripLeadsFile,
  dripErrorMessage, type ImportResult } from '../services/drip-api';

/**
 * Upload a file of leads into the drip sequence.
 *
 * EAB's leads reach the Admissions distribution list as email before Verity has them, so the team
 * already holds the names. This is what lets the sequence start without waiting on a Verity
 * integration nobody has built.
 *
 * ALWAYS PREVIEWS FIRST. Choosing a file shows exactly what would happen - every row, whether it is
 * new or already known, and every row that could not be read - and writes nothing. Registering only
 * happens on a second, deliberate click. A file contains real people, and a wrong column should be
 * caught before anyone is emailed rather than after.
 */
export default function DripLeadUploadDialog({
  open, onClose, onImported,
}: {
  open: boolean;
  onClose: () => void;
  onImported: (summary: string) => void;
}) {
  const [csv, setCsv] = useState('');
  const [xlsx, setXlsx] = useState('');
  const [blob, setBlob] = useState<{ kind: 'pdf' | 'eml'; b64: string } | null>(null);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const reset = () => {
    setCsv(''); setXlsx(''); setBlob(null); setFileName(''); setPreview(null); setError(null); setBusy(false);
    if (fileInput.current) fileInput.current.value = '';
  };

  const choose = async (file: File) => {
    setError(null); setPreview(null); setBusy(true); setFileName(file.name);
    try {
      // A workbook is a zip archive, so reading it with .text() would produce mojibake. Excel is
      // what Admissions actually have - EAB's exports arrive that way - so it is read as bytes and
      // flattened on the server rather than asking anyone to 'save as CSV' first.
      const asBase64 = async () => {
        const buf = new Uint8Array(await file.arrayBuffer());
        let bin = '';
        for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
        return btoa(bin);
      };
      // EAB sends leads as messages with PDF attachments, so both are read directly rather than
      // asking anyone to retype names - retyping is where wrong addresses come from.
      if (/\.(pdf|eml|msg)$/i.test(file.name)) {
        const kind = /\.pdf$/i.test(file.name) ? 'pdf' : 'eml';
        const b64 = await asBase64();
        setBlob({ kind, b64 }); setCsv(''); setXlsx('');
        setPreview(await importDripLeadsFile(kind, b64, false));
      } else if (/\.xlsx?$/i.test(file.name)) {
        const buf = new Uint8Array(await file.arrayBuffer());
        let bin = '';
        for (let i = 0; i < buf.length; i += 0x8000) {
          bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
        }
        const b64 = btoa(bin);
        setXlsx(b64); setCsv('');
        setPreview(await importDripLeadsXlsx(b64, false));
      } else {
        const text = await file.text();
        setCsv(text); setXlsx('');
        setPreview(await importDripLeads(text, false));
      }
    } catch (e) {
      setError(dripErrorMessage(e, 'That file could not be read.'));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true); setError(null);
    try {
      const result = blob ? await importDripLeadsFile(blob.kind, blob.b64, true)
                          : xlsx ? await importDripLeadsXlsx(xlsx, true)
                                 : await importDripLeads(csv, true);
      const parts = [`${result.imported ?? 0} registered`];
      if (result.updated) parts.push(`${result.updated} updated`);
      if (result.failed) parts.push(`${result.failed} failed`);
      onImported(parts.join(', '));
      reset();
      onClose();
    } catch (e) {
      setError(dripErrorMessage(e, 'The leads could not be registered.'));
    } finally {
      setBusy(false);
    }
  };

  const newCount = preview?.wouldImport ?? 0;
  const updateCount = preview?.wouldUpdate ?? 0;
  const problems = preview?.problems ?? [];

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: 'var(--text-primary)' }}>Upload a list of leads</DialogTitle>
      <DialogContent dividers>
        {/* EAB leads reach SIMS on their own once the importer hands them over, so this screen is
            for exceptions. The warning names the specific hazard rather than discouraging uploads
            in general: arrivedAt defaults to the moment of upload, so an older file starts its
            leads at day 0 today and they receive "still thinking about school?" weeks after they
            actually enquired. That is not visible anywhere afterwards - the record simply looks
            like a lead who arrived today - which is why it is said here, before the choice. */}
        <Alert severity="warning" sx={{ mb: 2 }}>
          <b>EAB leads arrive automatically — you do not need to upload them.</b>
          <Box component="ul" sx={{ m: '6px 0 0', pl: 2.5 }}>
            <li>Use this only when automatic intake has not worked, or for a list from another source.</li>
            <li>
              <b>Uploading an older file will start those leads from today</b>, not from when they
              enquired — so someone who enquired two weeks ago would begin at day&nbsp;0.
            </li>
          </Box>
        </Alert>
        <Typography variant="body2" sx={{ mb: 2, color: 'var(--text-secondary)' }}>
          A CSV or Excel file with one lead per row, or the EAB email or PDF itself. It needs an <b>Email</b> column; <b>First Name</b> and an
          inquiry date are used if present. Column names are matched loosely, so your own file
          should work without renaming anything.
        </Typography>

        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInput.current?.click()}
          disabled={busy}
          sx={{ color: 'var(--text-primary)', borderColor: 'var(--card-border)' }}
        >
          {fileName || 'Choose a file…'}
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,.xlsx,.xls,.pdf,.eml,text/csv,text/plain,application/pdf,message/rfc822,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          hidden
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void choose(f); }}
        />

        {busy && <Box sx={{ mt: 2 }}><CircularProgress size={22} /></Box>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        {preview && (
          <Box sx={{ mt: 2 }}>
            <Alert severity={newCount || updateCount ? 'info' : 'warning'} sx={{ mb: 2 }}>
              <b>Nothing has been saved yet.</b>{' '}
              {newCount || updateCount
                ? `${newCount} new lead${newCount === 1 ? '' : 's'} would start the sequence` +
                  (updateCount ? `, and ${updateCount} already registered would have their details refreshed without restarting.` : '.')
                : 'No leads in this file can be registered.'}
            </Alert>

            {preview.extractedFrom && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Read from {preview.extractedFrom === 'pdf' ? 'a PDF' : 'an email'}.{' '}
                <b>{preview.namesFound ?? 0} of {newCount + updateCount}</b> rows had a first name
                that the document actually labelled. The rest were left blank on purpose &mdash; a
                name guessed from a nearby line would greet the wrong person, which nothing later
                would catch. Check the addresses below against the source before confirming.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip label={`${newCount} new`} color={newCount ? 'primary' : 'default'} size="small" />
              <Chip label={`${updateCount} already registered`} size="small" />
              <Chip label={`${problems.length} rejected`} color={problems.length ? 'warning' : 'default'} size="small" />
            </Box>

            {problems.length > 0 && (
              <TableContainer sx={{ mb: 2, maxHeight: 200 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell><TableCell>Email</TableCell><TableCell>Why it was rejected</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {problems.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell>{p.row || '—'}</TableCell>
                        <TableCell>{p.email || '—'}</TableCell>
                        <TableCell sx={{ color: 'var(--error)' }}>{p.error}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {newCount > 0 && (
              <TableContainer sx={{ maxHeight: 260 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell><TableCell>Name</TableCell><TableCell>Email</TableCell>
                      {preview.extractedFrom && <TableCell>Found on</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(preview.newLeads ?? []).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{r.row ?? '—'}</TableCell>
                        <TableCell>{r.firstName || <i style={{ color: 'var(--text-secondary)' }}>no name</i>}</TableCell>
                        <TableCell>{r.email}</TableCell>
                        {preview.extractedFrom && (
                          <TableCell sx={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                            {r.context || '—'}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }} sx={{ color: 'var(--text-secondary)' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void confirm()}
          disabled={busy || !preview || (newCount + updateCount) === 0}
          sx={{ backgroundColor: 'var(--accent)', color: 'var(--button-text)' }}
        >
          {busy ? 'Registering…' : `Register ${newCount + updateCount} lead${newCount + updateCount === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
